import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { QrStyle, QrUserAgentRoutePreset } from './app.types';

@Component({
  selector: 'app-qr-code-form',
  standalone: false,
  template: `
    <form [formGroup]="form" (ngSubmit)="generate.emit()" class="form">
      <ion-list lines="full">
        <ion-item>
          <ion-label position="stacked">Title</ion-label>
          <ion-input formControlName="title" aria-label="Title" placeholder="Campaign or location"></ion-input>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">QR Destination</ion-label>
          <ion-input formControlName="targetUrl" aria-label="QR destination" placeholder="https://example.com or myapp://offer/123"></ion-input>
        </ion-item>

        <ion-item *ngIf="canSkipResponseValidation">
          <label class="checkbox-row">
            <input type="checkbox" formControlName="skipResponseValidation">
            <span>Skip website response check</span>
          </label>
          <ion-note color="medium">Keeps URL format checks, but skips DNS and HTTP reachability checks.</ion-note>
        </ion-item>

        <ion-item *ngIf="canManageUserAgentRoutes">
          <ion-label position="stacked">User Agent Routes</ion-label>
          <ion-select formControlName="userAgentPreset" interface="popover" aria-label="User agent routes" placeholder="Choose a route type">
            <ion-select-option *ngFor="let preset of userAgentPresets" [value]="preset.id">
              {{ preset.label }}
            </ion-select-option>
          </ion-select>
        </ion-item>

        <ion-item *ngIf="canManageUserAgentRoutes">
          <ion-label position="stacked">Route Destination</ion-label>
          <ion-input formControlName="userAgentTargetUrl" aria-label="Route destination" placeholder="myapp://offer or https://example.com/mobile"></ion-input>
        </ion-item>

        <ion-item *ngIf="canManageUserAgentRoutes">
          <ion-label position="stacked">Redirect Percentage</ion-label>
          <ion-input
            type="number"
            inputmode="numeric"
            formControlName="userAgentRedirectPercentage"
            aria-label="Redirect percentage"
            min="1"
            max="100"
            step="1"
            placeholder="100"
            (ionInput)="routeFrequencyChanged('percentage')"
          ></ion-input>
        </ion-item>

        <ion-item *ngIf="canManageUserAgentRoutes">
          <ion-label position="stacked">Every Nth Scan</ion-label>
          <ion-input
            type="number"
            inputmode="numeric"
            formControlName="userAgentEveryNthScan"
            aria-label="Every nth scan"
            min="1"
            max="10000"
            step="1"
            placeholder="Optional"
            (ionInput)="routeFrequencyChanged('nth')"
          ></ion-input>
        </ion-item>

        <ion-item *ngIf="canManageUserAgentRoutes">
          <ion-button type="button" fill="outline" (click)="addUserAgentRoute.emit()">Add User Agent Route</ion-button>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Icon Area Size</ion-label>
          <ion-input
            type="number"
            inputmode="numeric"
            formControlName="qrIconSize"
            aria-label="Icon area size"
            min="12"
            max="32"
            step="1"
          ></ion-input>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Logo</ion-label>
          <input #logoInput class="file-input" type="file" accept="image/*" aria-label="QR code logo" (change)="logoSelected.emit($event)">
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Style</ion-label>
          <ion-select formControlName="qrStyle" interface="popover" aria-label="Style">
            <ion-select-option *ngFor="let style of qrStyles" [value]="style.id">
              {{ style.label }}
            </ion-select-option>
          </ion-select>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Categories</ion-label>
          <ion-input formControlName="categoryNames" aria-label="Categories" placeholder="Spring, postcards, store launch"></ion-input>
        </ion-item>

        <ion-item>
          <label class="checkbox-row" [class.disabled]="form.controls['trackAnalytics'].disabled">
            <input type="checkbox" formControlName="trackAnalytics">
            <span>Use tracked redirect analytics</span>
          </label>
        </ion-item>

        <ion-item *ngIf="form.controls['trackAnalytics'].value">
          <label class="checkbox-row">
            <input type="checkbox" formControlName="providedByPage">
            <span>Show a provided-by page before redirecting</span>
          </label>
          <ion-note color="medium">Adds a brief Wahl Advertising page before redirecting. This helps support the site owner.</ion-note>
        </ion-item>
      </ion-list>

      <div class="category-picker" *ngIf="categorySuggestions.length > 0">
        <button
          type="button"
          class="category-chip"
          [class.active]="categorySelected(categoryName)"
          *ngFor="let categoryName of categorySuggestions"
          (click)="addCategory.emit(categoryName)"
        >
          {{ categoryName }}
        </button>
      </div>

      <div class="route-list" *ngIf="canManageUserAgentRoutes && userAgentRouteSummaries.length > 0">
        <span class="route-chip" *ngFor="let route of userAgentRouteSummaries; let routeIndex = index">
          <span class="route-chip-label">{{ route }}</span>
          <button type="button" (click)="removeUserAgentRoute.emit(routeIndex)">Remove</button>
        </span>
      </div>

      <ion-note color="danger" *ngIf="errorMessage">{{ errorMessage }}</ion-note>

      <div class="actions">
        <ion-button type="submit" [disabled]="isSaving">
          <ion-spinner *ngIf="isSaving" name="crescent"></ion-spinner>
          <span *ngIf="!isSaving">Generate</span>
        </ion-button>
        <ion-button type="button" fill="outline" [disabled]="!generatedImageUrl" (click)="download.emit()">
          Download PNG
        </ion-button>
      </div>
    </form>
  `,
  styles: [`
    .actions {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: 12px;
      margin-top: 20px;
    }

  `]
})
export class QrCodeFormComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) qrStyles: QrStyle[] = [];
  @Input() errorMessage = '';
  @Input() generatedImageUrl = '';
  @Input() isSaving = false;
  @Input() categorySuggestions: string[] = [];
  @Input() selectedCategoryNames: string[] = [];
  @Input() userAgentPresets: QrUserAgentRoutePreset[] = [];
  @Input() userAgentRouteSummaries: string[] = [];
  @Input() canManageUserAgentRoutes = false;
  @Input() canSkipResponseValidation = false;

  @Output() generate = new EventEmitter<void>();
  @Output() logoSelected = new EventEmitter<Event>();
  @Output() download = new EventEmitter<void>();
  @Output() addCategory = new EventEmitter<string>();
  @Output() addUserAgentRoute = new EventEmitter<void>();
  @Output() removeUserAgentRoute = new EventEmitter<number>();

  @ViewChild('logoInput') private readonly logoInput?: ElementRef<HTMLInputElement>;

  clearLogoInput(): void {
    if (this.logoInput?.nativeElement) {
      this.logoInput.nativeElement.value = '';
    }
  }

  categorySelected(categoryName: string): boolean {
    return this.selectedCategoryNames.some((name) => name.toLowerCase() === categoryName.toLowerCase());
  }

  routeFrequencyChanged(mode: 'percentage' | 'nth'): void {
    const sourceControlName = mode === 'percentage' ? 'userAgentRedirectPercentage' : 'userAgentEveryNthScan';
    const targetControlName = mode === 'percentage' ? 'userAgentEveryNthScan' : 'userAgentRedirectPercentage';
    const sourceValue = String(this.form.controls[sourceControlName]?.value || '').trim();

    if (sourceValue) {
      this.form.controls[targetControlName]?.setValue('');
    }
  }
}
